import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Lock, Mail, Loader2, AlertCircle, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [step, setStep] = useState('credentials'); // 'credentials' or 'mfa'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if already logged in as admin
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        email,
        password
      });

      if (response.data.mfa_required) {
        setSessionId(response.data.session_id);
        setStep('mfa');
        toast.info('Please enter the MFA code to continue');
      } else {
        // Direct login (fallback)
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
        toast.success('Welcome to Admin Dashboard');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed. Please check your credentials.');
      toast.error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/verify-mfa`, {
        session_id: sessionId,
        mfa_code: mfaCode
      });

      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
      toast.success('MFA verified! Welcome to Admin Dashboard');
      navigate('/admin/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'MFA verification failed.');
      toast.error('MFA verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setMfaCode('');
    setSessionId('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl mb-4 shadow-lg shadow-indigo-500/30">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">GlobalVyapar</h1>
          <p className="text-slate-400 text-sm">Admin Control Panel</p>
        </div>

        {/* Login Card */}
        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {step === 'mfa' && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleBackToCredentials}
                  className="text-slate-400 hover:text-white -ml-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              {step === 'credentials' ? 'Admin Login' : 'Two-Factor Authentication'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {step === 'credentials' 
                ? 'Enter your admin credentials to access the dashboard'
                : 'Enter the MFA code to verify your identity'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'credentials' ? (
              <form onSubmit={handleCredentialsSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-400" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@globalvyapar.co.in"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                      required
                      data-testid="admin-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
                      required
                      data-testid="admin-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  data-testid="admin-login-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Continue to MFA
                    </>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleMFASubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-400" />
                    <p className="text-sm text-red-400">{error}</p>
                  </div>
                )}

                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <KeyRound className="h-5 w-5 text-indigo-400" />
                    <p className="text-sm font-medium text-indigo-300">MFA Required</p>
                  </div>
                  <p className="text-xs text-slate-400">
                    Enter your MFA code to verify your identity.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mfaCode" className="text-slate-300">MFA Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="mfaCode"
                      type="text"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value.toUpperCase().slice(0, 6))}
                      placeholder="Enter MFA code"
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="admin-mfa-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading || mfaCode.length < 4}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  data-testid="admin-mfa-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Access Dashboard
                    </>
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 pt-4 border-t border-slate-700">
              <p className="text-xs text-slate-500 text-center">
                This area is restricted to authorized personnel only.
                <br />
                All access attempts are logged and monitored.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Security Badge */}
        <div className="flex items-center justify-center gap-2 mt-6">
          <Shield className="h-4 w-4 text-green-500" />
          <p className="text-xs text-slate-500">
            Protected by MFA • Audit Logging Enabled
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-4">
          © 2026 GlobalVyapar. Admin Panel v2.0
        </p>
      </div>
    </div>
  );
}
